import { type Ref, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { hblMapProducts, hblMapWishlist, useCustomer, useNotification, useCookie, useRuntimeConfig } from '#imports'
import type { HblWishlist, HblIUseWishlist, HblProduct } from '@/utils/types'
import { ProductShopware, WishlistShopware } from '@hubblecommerce/hubble/platforms/shopware/api-client'

export const useWishlist = defineStore('use-wishlist', (): HblIUseWishlist => {
    const wishlist: Ref<HblWishlist | null> = ref(null)
    const miniWishlist: Ref<string[]> = ref([])

    const { wishlistCookie } = useRuntimeConfig().public as { wishlistCookie: { name: string, options: any } }
    const error: Ref = ref(false)
    const loading: Ref<boolean> = ref(false)
    const { showNotification } = useNotification()

    const associations = {
        media: {},
        sets: {},
        manufacturer: {
            associations: {
                media: {}
            }
        }
    }

    async function getWishlist (): Promise<HblWishlist | null | void> {
        loading.value = true
        error.value = false

        try {
            let mappedData = null

            const customerStore = useCustomer()
            const { customer } = storeToRefs(customerStore)

            if (customer?.value != null) {
                // @ts-ignore
                const response = await WishlistShopware.readCustomerWishlist({
                    associations
                })

                mappedData = hblMapWishlist(response)
                wishlist.value = mappedData
            } else {
                if (miniWishlist?.value?.length === 0) {
                    loading.value = false

                    return {
                        products: []
                    }
                }

                const queries: any = []

                miniWishlist.value.map((productId) => {
                    queries.push({
                        type: 'equals',
                        field: 'id',
                        value: productId
                    })
                })

                const response = await ProductShopware.readProduct({
                    filter: [
                        {
                            type: 'multi',
                            // @ts-ignore
                            operator: 'or',
                            queries
                        }
                    ],
                    associations
                })

                if (response?.elements != null) {
                    mappedData = {
                        products: hblMapProducts(response.elements)
                    }
                    wishlist.value = mappedData
                }
            }

            loading.value = false

            return mappedData
        } catch (e) {
            handleWishlistApiError(e)
        }
    }

    async function addToWishlist (productId: string): Promise<void> {
        error.value = false
        loading.value = true

        try {
            const customerStore = useCustomer()
            const { customer } = storeToRefs(customerStore)

            if (customer?.value != null) {
                await WishlistShopware.addProductOnWishlist(productId)

                // update wishlist because endpoint doesn't answer with the new wishlist
                // @ts-ignore
                const response = await WishlistShopware.readCustomerWishlist({
                    associations
                })
                wishlist.value = hblMapWishlist(response)
            } else {
                if (!miniWishlist.value.includes(productId)) {
                    miniWishlist.value.push(productId)
                    saveWishlist()
                }
            }

            loading.value = false
        } catch (e) {
            handleWishlistApiError(e)
            showNotification(error.value, 'error')
        }
    }

    async function removeFromWishlist (productId: string): Promise<void> {
        error.value = false
        loading.value = true

        try {
            const customerStore = useCustomer()
            const { customer } = storeToRefs(customerStore)

            if (customer?.value != null) {
                await WishlistShopware.deleteProductOnWishlist(productId)

                // update wishlist because endpoint doesn't answer with the new wishlist
                // @ts-ignore
                const response = await WishlistShopware.readCustomerWishlist({
                    associations
                })
                wishlist.value = hblMapWishlist(response)
            } else {
                const index = wishlist?.value?.products?.findIndex((item) => item.id === productId)
                const indexMini = miniWishlist?.value?.findIndex((item) => item === productId)

                if (indexMini != null && indexMini !== -1) {
                    miniWishlist.value.splice(indexMini, 1)

                    if (index != null && index !== -1) {
                        wishlist?.value?.products.splice(index, 1)
                    }

                    saveWishlist()
                }
            }

            loading.value = false   
        } catch (e) {
            handleWishlistApiError(e)
            showNotification(error.value, 'error')
        }
    }

    function handleWishlistApiError (e: any) {
        if (e.statusCode === 404) {
            wishlist.value = null
        } else if (e.statusCode === 403) {
            const customerStore = useCustomer()
            const { customer } = storeToRefs(customerStore)

            if (customer?.value != null) {
                error.value = 'Wishlist is currently not available'
            } else {
                error.value = 'Register or login to create your own wishlist'
            }
            clearWishlist()
        } else {
            error.value = e
        }
        loading.value = false
    }

    function saveWishlist (): void {
        // @ts-ignore
        const cookie = useCookie(wishlistCookie.name, wishlistCookie.options)

        const customerStore = useCustomer()
        const { customer } = storeToRefs(customerStore)
        if (customer?.value != null) {
            miniWishlist.value = wishlist.value ? wishlist.value?.products?.map((product: HblProduct) => product.id) : []
        }

        // @ts-ignore
        cookie.value = miniWishlist.value
    }

    async function mergeWishlist (productIds: string[]): Promise<boolean> {
        error.value = false
        loading.value = true

        try {
            await WishlistShopware.mergeProductOnWishlist({
                productIds
            })

            return true
        } catch (e: any) {
            showNotification(error.value, 'error')
            return false
        }
    }

    function clearWishlist (): void {
        wishlist.value = null
        miniWishlist.value = []
        saveWishlist()
    }

    if (import.meta.client) {
        watch(wishlist, () => {
            saveWishlist()
        })
    }

    return {
        wishlist,
        miniWishlist,
        getWishlist,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        mergeWishlist,
        loading,
        error
    }
})
